import styled, { keyframes } from 'styled-components'

export const GRADIENT_COLORS = {
  start: '#7C5CE2',
  mid: '#A88CFF',
  end: '#7958DF',
} as const
export const CARD_GRADIENT = `linear-gradient(225deg, ${GRADIENT_COLORS.start} 0%, ${GRADIENT_COLORS.mid} 50%, ${GRADIENT_COLORS.end} 100%)`

export const makeCardGradient = (colors: {
  start: string
  mid: string
  end: string
}) =>
  `linear-gradient(225deg, ${colors.start} 0%, ${colors.mid} 50%, ${colors.end} 100%)`

export const CARD_HEIGHT = 365
export const SEPARATOR_Y = 285
export const NOTCH_RADIUS = 15

const pulse = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.08); }
`

export const Styles = {
  Container: styled.div`
    position: relative;
    width: 280px;
    height: ${CARD_HEIGHT}px;
    user-select: none;
    touch-action: none;
    overscroll-behavior: none;
    -webkit-tap-highlight-color: transparent;
    margin: 0 auto;
    cursor: pointer;
  `,

  Animation: styled.div<{ $filter?: string }>`
    position: absolute;
    z-index: 3;
    left: 50%;
    top: 40%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    animation: ${pulse} 1.2s ease-in-out infinite;
    ${({ $filter }) => $filter && `filter: ${$filter};`}
  `,

  HandCursor: styled.div`
    position: absolute;
    z-index: 3;
    left: 50%;
    top: 40%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4));
    transition: left 0.2s linear, top 0.2s linear;
  `,

  RevealedCard: styled.div<{ $gradient?: string }>`
    position: relative;
    width: 280px;
    height: ${CARD_HEIGHT}px;
    border-radius: 28px;
    overflow: visible;
    background: ${({ $gradient }) => $gradient ?? CARD_GRADIENT};
  `,

  CardTop: styled.div`
    position: absolute;
    inset: 0 0 ${CARD_HEIGHT - SEPARATOR_Y}px 0;
    border-radius: 28px 28px 0 0;
    overflow: hidden;
  `,

  Separator: styled.div<{ $isDark?: boolean }>`
    position: absolute;
    top: ${SEPARATOR_Y}px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;
    color: ${({ $isDark }) => ($isDark ? 'rgba(107, 107, 107, 1)' : '#fff')};

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      width: ${NOTCH_RADIUS * 2}px;
      height: ${NOTCH_RADIUS * 2}px;
      border-radius: 50%;
      background: ${({ $isDark }) => ($isDark ? '#6b6b6b' : '#fff')};
      transform: translateY(-50%);
      z-index: 2;
    }

    &::before {
      left: -${NOTCH_RADIUS}px;
    }

    &::after {
      right: -${NOTCH_RADIUS}px;
    }
  `,

  CardBottom: styled.div<{ $finished?: boolean; $overlayColor?: string }>`
    position: absolute;
    top: ${SEPARATOR_Y}px;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0 0 28px 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    background: ${({ $finished, $overlayColor }) =>
      $finished ? 'transparent' : $overlayColor ?? 'rgba(0, 0, 0, 0.3)'};
    transition: background 0.3s ease;
  `,

  CardBottomContent: styled.div<{ $visible?: boolean }>`
    width: 100%;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.3s ease;
  `,

  PromoBox: styled.div`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    padding: 6px 12px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,

  PromoLabel: styled.p`
    font-size: 11px;
    font-weight: 400;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.6);
    text-align: center;
    margin-bottom: 2px;
  `,

  PromoCode: styled.p`
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 6px;
  `,

  Text: styled.div<{ $visible?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${SEPARATOR_Y}px;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.3s ease;
    padding: 16px;
  `,

  TopBox: styled.div<{ $bordered?: boolean; $gradient?: string }>`
    width: 100%;
    height: 100%;
    background: ${({ $gradient }) => $gradient ?? CARD_GRADIENT};
    border-radius: 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    box-shadow: inset 0 0 0 1px
      ${({ $bordered }) =>
        $bordered ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0)'};
    transition: box-shadow 0.3s ease;
  `,

  DiscountRow: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: center;
  `,

  Discount: styled.span`
    font-size: 80px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
  `,

  DiscountPercent: styled.span`
    font-size: 48px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
  `,

  DiscountWord: styled.p`
    text-align: center;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.1;
    color: #fff;
  `,

  PlanName: styled.p`
    position: absolute;
    bottom: 16px;
    left: 16px;
    right: 16px;
    text-align: center;
    font-size: 18px;
    font-weight: 400;
    line-height: 1.4;
    color: #fff;
  `,
} as const
